/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { buildStorefrontMediaUrl } from '@/lib/page-designer/attribute-resolution-context';

export type PdMediaSource = string | Record<string, unknown> | undefined;

export type PdMediaUrlOptions = {
    host?: string;
    siteId?: string;
    locale?: string;
};

function isPdFileEnvelope(record: Record<string, unknown>): boolean {
    const media = record.media as Record<string, unknown> | undefined;

    return (
        media != null &&
        typeof media.libraryDomain === 'string' &&
        typeof media.path === 'string' &&
        !('focalPoint' in record) &&
        !('metaData' in record)
    );
}

/** Ensures BM-pasted URLs without a scheme still load in the browser. */
export function normalizePdExternalMediaUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) {
        return '';
    }
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    if (trimmed.startsWith('//')) {
        return `https:${trimmed}`;
    }
    if (/^[a-z0-9.-]+\.[a-z]{2,}(\/|$)/i.test(trimmed)) {
        return `https://${trimmed}`;
    }
    return trimmed;
}

/**
 * Resolves Page Designer `image` / `file` attribute payloads to a playable URL string.
 */
export function resolvePdMediaUrl(value: PdMediaSource, options?: PdMediaUrlOptions): string {
    if (!value) {
        return '';
    }
    if (typeof value === 'string') {
        return normalizePdExternalMediaUrl(value);
    }

    const record = value;
    const nestedFile = record.file as Record<string, unknown> | undefined;
    const path = (record.path as string) || (nestedFile?.path as string);

    if (isPdFileEnvelope(record) && options?.host && options?.siteId) {
        const media = record.media as { libraryDomain: string; path: string };
        return buildStorefrontMediaUrl(
            { libraryDomain: media.libraryDomain, path: media.path, locale: options.locale },
            { host: options.host, siteId: options.siteId, locale: options.locale ?? 'default' }
        );
    }

    const resolved = (
        (record.absURL as string) ||
        (record.url as string) ||
        (record.disBaseLink as string) ||
        (record.link as string) ||
        (nestedFile?.absURL as string) ||
        (nestedFile?.url as string) ||
        (typeof path === 'string' && (path.startsWith('/') || path.startsWith('http')) ? path : '') ||
        ''
    ).trim();

    return resolved ? normalizePdExternalMediaUrl(resolved) : '';
}

/**
 * Page Designer media: explicit external URL wins; otherwise content-library / file envelope.
 */
export function resolvePdMediaWithExternal(
    librarySrc: PdMediaSource,
    externalSrc: string | undefined,
    options?: PdMediaUrlOptions
): string {
    const external = normalizePdExternalMediaUrl(externalSrc ?? '');
    if (external) {
        return external;
    }
    return resolvePdMediaUrl(librarySrc, options);
}
